/**
 * WebinarLanding — KÜLÖN duplikált webinar landing oldal.
 *
 * ⚠️ FONTOS: az eredeti publikus landing (`/app/frontend/src/pages/LandingHome.js`,
 * `/` route) ÉRINTETLEN marad. Ez a fájl egy biztonságos másolat, amelyen a
 * webinar-specifikus változtatások (cím, CTA, email-form, kolibri-pose,
 * piacszöveg) egy következő körben kerülnek implementálásra.
 *
 * Route: `/webinar` (public, NEM Layout-wrappelt - sidebar/header chrome nélkül).
 * Köszönő oldal: `/webinar-thankyou` → `WebinarThankYou.js`.
 *
 * Az alábbi kódbázis innen a `LandingHome.js`-ből másolva (2026-02). A vizuális
 * rendszer (világos lavender háttér, MagicalBackdrop, kolibri SVG, glow,
 * fontok, lila CTA, prémium UI tokenek) megegyezik a publikus landinggel.
 * A `data-testid` értékek `webinar-` prefix-szel készülnek, hogy a meglévő
 * landing E2E tesztek ne ütközzenek.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { trackPageView, METAPIXEL_NOSCRIPT_URL } from "../lib/metaPixel";

/* ════════════════════════════════════════════════════════════════════
 * WebinarCountdown — szupervékony, kompakt vízszintes pill a header
 * jobb oldalán. Számok és labelek közel egymáshoz, diszkrét.
 * ══════════════════════════════════════════════════════════════════ */
// 2026. augusztus 13. csütörtök 19:00 Europe/Budapest (CEST = UTC+2) = 17:00 UTC.
// Timezone-safe: ugyanahhoz az abszolút időponthoz számol vissza, bárhonnan.
const WEBINAR_TARGET_TS = Date.parse("2026-08-13T17:00:00Z");
function pad(n) { return String(n).padStart(2, "0"); }
function WebinarCountdown() {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, WEBINAR_TARGET_TS - now);
  const days  = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins  = Math.floor((diff % 3600000) / 60000);
  const secs  = Math.floor((diff % 60000) / 1000);
  const parts = [
    [pad(days),  "nap"],
    [pad(hours), "óra"],
    [pad(mins),  "perc"],
    [pad(secs),  "mp"],
  ];
  return (
    <div
      data-testid="webinar-countdown"
      className="flex items-baseline gap-2"
      style={{ fontFamily: "inherit" }}
    >
      {parts.map(([v, label], i) => (
        <React.Fragment key={label}>
          <div className="flex flex-col items-center gap-0.5">
            <span
              className="font-heading"
              style={{
                fontSize: "clamp(22px, 1.8vw, 28px)",
                fontWeight: 300,
                letterSpacing: "-0.02em",
                lineHeight: 1,
                color: "#1B1733",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {v}
            </span>
            <span
              style={{
                fontSize: 11,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#6F6790",
                fontWeight: 500,
              }}
            >
              {label}
            </span>
          </div>
          {i < parts.length - 1 && (
            <span
              style={{
                color: "rgba(94,80,247,0.32)",
                fontSize: 15,
                fontWeight: 300,
                lineHeight: 1,
              }}
            >
              ·
            </span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * WebinarRegisterModal — popup modal Keresztnév + E-mail mezőkkel.
 * Submit → /webinar-thankyou. ESC + overlay-click + X bezárja.
 * Visual: MEX Magic prémium glass + lila accent.
 * ══════════════════════════════════════════════════════════════════ */
function WebinarRegisterModal({ open, onClose }) {
  const navigate = useNavigate();
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const canSubmit =
    firstName.trim().length > 0 && /.+@.+\..+/.test(email) && consent && !submitting;

  // ESC bezárás
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    // Body scroll lock
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const onSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (firstName.trim().length === 0) {
      setFormError("Kérlek, add meg a keresztneved.");
      return;
    }
    if (!/.+@.+\..+/.test(email)) {
      setFormError("Kérlek, adj meg egy érvényes e-mail címet.");
      return;
    }
    if (!consent) {
      setConsentError(true);
      return;
    }
    setConsentError(false);

    // Collect UTM params from current URL — light-touch attribution.
    const utm = {};
    try {
      const sp = new URLSearchParams(window.location.search);
      ["utm_source","utm_medium","utm_campaign","utm_content","utm_term"].forEach((k) => {
        const v = sp.get(k);
        if (v) utm[k] = v;
      });
    } catch { /* SSR-safety, ignore */ }

    setSubmitting(true);
    try {
      await api.post("/webinar/subscribe", {
        first_name: firstName.trim(),
        email: email.trim().toLowerCase(),
        phone: (phone || "").trim() || null,
        consent: true,
        ...utm,
      });
      navigate("/webinar-thankyou");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setFormError(
        (typeof detail === "string" && detail) ||
        "Valami nem sikerült. Kérlek, próbáld meg újra pár pillanat múlva."
      );
      setSubmitting(false);
    }
  };

  const inputStyle = {
    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(94,80,247,0.22)",
    color: "#1B1733",
    fontSize: "15px",
    padding: "13px 15px",
    borderRadius: 12,
    outline: "none",
    width: "100%",
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
  };

  return (
    <div
      data-testid="webinar-register-modal-overlay"
      onClick={onClose}
      className="fixed inset-0 z-[1000] flex items-center justify-center px-4"
      style={{
        background: "rgba(27, 23, 51, 0.55)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        data-testid="webinar-register-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="webinar-modal-title"
        className="relative w-full max-w-[440px] rounded-3xl"
        style={{
          background:
            "linear-gradient(180deg, #FFFFFF 0%, #FBF9FF 100%)",
          border: "1px solid rgba(232,228,250,0.95)",
          boxShadow:
            "0 40px 100px -20px rgba(60,52,137,0.45), 0 12px 30px -10px rgba(60,52,137,0.20), 0 1px 0 rgba(255,255,255,0.85) inset",
          padding: "32px 30px 28px",
        }}
      >
        {/* Bezárás gomb */}
        <button
          type="button"
          onClick={onClose}
          data-testid="webinar-register-modal-close"
          aria-label="Bezárás"
          className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-colors"
          style={{
            background: "rgba(94,80,247,0.08)",
            color: "#5e50f7",
            fontSize: 18,
            lineHeight: 1,
          }}
        >
          ×
        </button>

        <h3
          id="webinar-modal-title"
          className="font-heading"
          style={{
            fontSize: 22,
            fontWeight: 700,
            color: "#1B1733",
            letterSpacing: "-0.02em",
            lineHeight: 1.2,
            marginBottom: 6,
          }}
        >
          Lefoglalom a helyem
        </h3>
        <p
          style={{
            fontSize: 13.5,
            color: "#6F6790",
            lineHeight: 1.55,
            marginBottom: 20,
          }}
        >
          2026. augusztus 13. csütörtök, 19:00 - ingyenes AI marketing mesterkurzus.
        </p>

        <form
          onSubmit={onSubmit}
          data-testid="webinar-register-form"
          className="flex flex-col gap-3"
        >
          <input
            type="text"
            placeholder="Keresztnév"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            data-testid="webinar-register-firstname"
            style={inputStyle}
            autoFocus
          />
          <input
            type="email"
            placeholder="E-mail"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="webinar-register-email"
            style={inputStyle}
          />
          <input
            type="tel"
            placeholder="Telefonszám (opcionális)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="webinar-register-phone"
            autoComplete="tel"
            style={inputStyle}
          />

          {/* Kötelező hozzájárulási checkbox */}
          <label
            className="flex items-start gap-2.5 mt-1 cursor-pointer select-none"
            style={{ fontSize: 12.5, color: "#3F365E", lineHeight: 1.5 }}
          >
            <input
              type="checkbox"
              checked={consent}
              onChange={(e) => {
                setConsent(e.target.checked);
                if (e.target.checked) setConsentError(false);
              }}
              data-testid="webinar-register-consent"
              style={{
                marginTop: 3,
                width: 16,
                height: 16,
                accentColor: "#5e50f7",
                cursor: "pointer",
                flexShrink: 0,
              }}
            />
            <span>
              Elfogadom az{" "}
              <a
                href="/adatkezelesi-tajekoztato"
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[#5e50f7] hover:underline"
              >
                Adatkezelési tájékoztatót
              </a>
              , és hozzájárulok, hogy e-mailben megkapjam a webinár részleteit és a kapcsolódó MEX Magic kommunikációkat.
            </span>
          </label>
          {consentError && (
            <div
              data-testid="webinar-consent-error"
              role="alert"
              style={{
                fontSize: 12,
                color: "#D62828",
                marginTop: -4,
                lineHeight: 1.4,
              }}
            >
              Kérlek, fogadd el az adatkezelési feltételeket a regisztrációhoz.
            </div>
          )}

          {formError && (
            <div
              data-testid="webinar-form-error"
              role="alert"
              style={{
                fontSize: 12.5,
                color: "#D62828",
                lineHeight: 1.45,
                marginTop: -2,
              }}
            >
              {formError}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="webinar-register-modal-submit"
            className="group inline-flex items-center justify-center gap-3 mt-1 px-8 py-4 rounded-2xl font-bold text-white transition-all hover:-translate-y-0.5"
            style={{
              background:
                "linear-gradient(135deg, #6E5BFF 0%, #5e50f7 30%, #7C3AED 65%, #4F46E5 100%)",
              boxShadow:
                "0 24px 60px rgba(94,80,247,0.48), 0 8px 18px rgba(124,58,237,0.32), 0 0 0 1px rgba(255,255,255,0.32) inset",
              fontSize: 16,
              opacity: canSubmit ? 1 : 0.55,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
          >
            {submitting ? "Hely foglalása..." : "Lefoglalom a helyem"}
            {!submitting && <span style={{ transition: "transform 0.15s ease" }}>→</span>}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * WebinarCtaButton — nagy, prémium lila CTA gomb, ami megnyitja a modalt.
 * ══════════════════════════════════════════════════════════════════ */
function WebinarCtaButton({ onClick, testid = "webinar-cta" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl font-bold text-white transition-all hover:-translate-y-0.5"
      style={{
        background:
          "linear-gradient(135deg, #6E5BFF 0%, #5e50f7 30%, #7C3AED 65%, #4F46E5 100%)",
        boxShadow:
          "0 24px 60px rgba(94,80,247,0.48), 0 8px 18px rgba(124,58,237,0.32), 0 0 0 1px rgba(255,255,255,0.32) inset",
        fontSize: "clamp(16px, 1.15vw, 18px)",
      }}
    >
      Lefoglalom a helyem
      <span style={{ transition: "transform 0.15s ease" }}>→</span>
    </button>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * WebinarValueSection — "A 60 perces mesterkurzus végére tisztábban
 * fogod látni:" + 3 prémium glass kártya.
 * ══════════════════════════════════════════════════════════════════ */
function WebinarValueSection() {
  const items = [
    {
      icon: Target,
      title: "Miért nem reagálnak az ügyfeleid?",
      text:
        "Megmutatom, miért nem elég ma már egyszerűen „jó tartalmat\u201d írni, és mitől lesz egy üzenet annyira pontos, hogy az ideális ügyfeled megálljon, magára ismerjen, és azt érezze: erre vártam már évek óta.",
    },
    {
      icon: MagicWand,
      title: "Hogyan lesz az AI-ból valódi segítség?",
      text:
        "Megnézzük, hogyan tudsz AI-jal gyorsan posztokat, emaileket és üzeneteket készíteni úgy, hogy a végeredmény ne legyen sablonos, hideg vagy robotízű.",
    },
    {
      icon: TrendUp,
      title: "Görcs nélküli ügyfélszerzés",
      text:
        "Megmutatom, hogyan tudsz egy délután alatt akár egy hónapnyi ügyfélszerző tartalmat előkészíteni, hogy ne naphosszat a gép előtt ülj, hanem több időd és energiád maradjon az ügyfeleidre, a vállalkozásodra és az életedre.",
    },
  ];
  return (
    <section
      data-testid="webinar-value-section"
      className="px-6 sm:px-10 lg:px-16 pt-20 lg:pt-24 pb-14 lg:pb-16"
      style={{ background: "linear-gradient(180deg, #F5EEFE 0%, #EBE4FA 100%)" }}
    >
      <div className="max-w-[1180px] mx-auto">
        <h2
          className="font-heading text-center mb-12"
          style={{
            fontSize: "clamp(26px, 2.6vw, 36px)",
            fontWeight: 700,
            color: "#1B1733",
            letterSpacing: "-0.02em",
            lineHeight: 1.18,
          }}
        >
          A 60 perces mesterkurzus végére tisztábban fogod látni:
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.map((it, idx) => {
            const Icon = it.icon;
            return (
              <div
                key={idx}
                data-testid={`webinar-value-card-${idx + 1}`}
                className="rounded-2xl"
                style={{
                  background: "rgba(255,255,255,0.65)",
                  border: "1px solid rgba(255,255,255,0.78)",
                  boxShadow: "0 20px 50px -20px rgba(94,80,247,0.16)",
                  padding: "26px 24px 24px",
                  backdropFilter: "blur(8px)",
                  WebkitBackdropFilter: "blur(8px)",
                }}
              >
                <div
                  className="inline-flex items-center justify-center mb-5"
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background:
                      "linear-gradient(135deg, rgba(94,80,247,0.18) 0%, rgba(124,58,237,0.12) 100%)",
                    border: "1px solid rgba(94,80,247,0.32)",
                    boxShadow:
                      "0 8px 22px -10px rgba(94,80,247,0.45), inset 0 1px 0 rgba(255,255,255,0.7)",
                  }}
                >
                  <Icon size={24} weight="duotone" color="#5e50f7" />
                </div>
                <h3
                  className="font-heading mb-2"
                  style={{ fontSize: 18, fontWeight: 700, color: "#1B1733", letterSpacing: "-0.01em", lineHeight: 1.3 }}
                >
                  {it.title}
                </h3>
                <p style={{ fontSize: 14.5, lineHeight: 1.6, color: "#433C66" }}>
                  {it.text}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * WebinarLiveOnlyCallout — „Fontos: gyere élőben, mert erről a
 * mesterkurzusról nem készül felvétel."
 * A `WebinarValueSection` után illesztve, közvetlenül az About előtt.
 * Prémium világos háttér, ikon + kiemelt „Fontos:" szó. Nem FOMO-banner,
 * elegáns megjegyzés.
 * ══════════════════════════════════════════════════════════════════ */
function WebinarLiveOnlyCallout() {
  return (
    <section
      data-testid="webinar-live-only-callout"
      className="px-6 sm:px-10 lg:px-16 py-8 lg:py-10"
      style={{
        background:
          "linear-gradient(180deg, rgba(245,240,255,0.7) 0%, rgba(233,224,252,0.6) 100%)",
      }}
    >
      <div className="max-w-[880px] mx-auto">
        <div
          className="flex items-start gap-3 sm:gap-4 rounded-[18px]"
          style={{
            background: "rgba(255,255,255,0.85)",
            border: "1px solid rgba(94,80,247,0.20)",
            boxShadow: "0 8px 24px -12px rgba(60,52,137,0.14)",
            padding: "18px 22px",
            backdropFilter: "blur(6px)",
          }}
        >
          <div
            className="inline-flex items-center justify-center shrink-0"
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background:
                "linear-gradient(135deg, rgba(94,80,247,0.14) 0%, rgba(124,58,237,0.10) 100%)",
              border: "1px solid rgba(94,80,247,0.20)",
            }}
          >
            <Warning size={20} weight="duotone" color="#5e50f7" />
          </div>
          <p
            style={{
              fontSize: 15,
              lineHeight: 1.55,
              color: "#2A2350",
              margin: 0,
              alignSelf: "center",
            }}
          >
            <strong style={{ color: "#1B1733", fontWeight: 700 }}>
              Fontos:
            </strong>{" "}
            gyere élőben, mert erről a mesterkurzusról nem készül felvétel.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════════════════════════════════════════════
 * WebinarAboutSection — Dr. Mózes Tamás bemutatkozás 2-oszloposan
 *   - Bal: prémium portré fotó (rounded, lila glow keret)
 *   - Jobb: cím + szöveg + záró CTA
 * ══════════════════════════════════════════════════════════════════ */
const TAMAS_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_mex-foundation/artifacts/t21042ji_brandagency-render%20%2842%29.png";

function WebinarAboutSection({ onOpenRegister }) {
  return (
    <section
      data-testid="webinar-about-section"
      className="px-6 sm:px-10 lg:px-16 py-16 lg:py-24"
      style={{ background: "linear-gradient(180deg, #E5DDF6 0%, #DED4F2 100%)" }}
    >
      <div className="max-w-[1120px] mx-auto grid grid-cols-1 md:grid-cols-[42%_58%] gap-10 md:gap-14 items-center">
        {/* Bal oszlop: portré */}
        <div className="relative">
          {/* Soft lila glow a fotó mögött */}
          <div
            aria-hidden="true"
            className="absolute -inset-4 rounded-[28px]"
            style={{
              background:
                "radial-gradient(60% 60% at 50% 50%, rgba(94,80,247,0.28) 0%, rgba(124,58,237,0.12) 50%, rgba(94,80,247,0) 80%)",
              filter: "blur(20px)",
              zIndex: 0,
            }}
          />
          <div
            className="relative rounded-[24px] overflow-hidden"
            style={{
              border: "1px solid rgba(255,255,255,0.75)",
              boxShadow:
                "0 30px 80px -20px rgba(60,52,137,0.32), 0 8px 24px -8px rgba(60,52,137,0.18)",
              zIndex: 1,
              aspectRatio: "1 / 1",
              maxWidth: 440,
              marginInline: "auto",
            }}
            data-testid="webinar-about-photo"
          >
            <img
              src={TAMAS_PORTRAIT}
              alt="Dr. Mózes Tamás portré"
              loading="lazy"
              decoding="async"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center top",
                display: "block",
              }}
            />
          </div>
        </div>

        {/* Jobb oszlop: szöveg */}
        <div>
          <div
            data-testid="webinar-about-eyebrow"
            style={{
              fontSize: 11.5,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "#5e50f7",
              fontWeight: 600,
              marginBottom: 14,
            }}
          >
            A mesterkurzus tartója
          </div>
          <h2
            className="font-heading mb-6"
            style={{
              fontSize: "clamp(26px, 2.6vw, 36px)",
              fontWeight: 700,
              color: "#1B1733",
              letterSpacing: "-0.02em",
              lineHeight: 1.18,
            }}
          >
            Dr. Mózes Tamás vagyok
          </h2>
          <div
            className="space-y-4 mb-9"
            style={{ fontSize: 15.5, lineHeight: 1.7, color: "#3F365E" }}
          >
            <p>
              Az elmúlt években több száz coach, szakértő és szolgáltatásalapú vállalkozó fordult hozzánk, hogy magasabb szintre emelje a vállalkozását.
            </p>
            <p>
              A közös nevező szinte mindig ugyanaz: tisztább üzenet, erősebb kommunikáció és egy olyan ügyfélszerző rendszer, amely nem elveszi az energiát, hanem végre megtámasztja a vállalkozót.
            </p>
            <p>
              Ezen a mesterkurzuson azt mutatom meg, hogyan tudod mindezt AI-jal gyorsabban, könnyebben és a saját hangod megtartásával felépíteni.
            </p>
          </div>
          <WebinarCtaButton onClick={onOpenRegister} testid="webinar-cta-bottom" />
        </div>
      </div>
    </section>
  );
}


import {
  Sparkle,
  Lightning,
  ChatCircleDots,
  User as UserIcon,
  EnvelopeSimple,
  PencilSimple,
  Tag,
  CheckCircle,
  ArrowRight,
  Heart,
  ChatCircle,
  ShareNetwork,
  Globe,
  PaperPlaneTilt,
  TrendUp,
  InstagramLogo,
  EnvelopeOpen,
  Target,
  MagicWand,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
// useAuth NEM kell — /webinar PUBLIC route, semmilyen auth-függő redirect nincs.
import LoginModal from "../components/LoginModal";

// 14 rövidített, stabilan törhető headline - layout-shift mentes rotációhoz
const HEADLINES = [
  "Mágnes posztok",
  "Killer webcopy",
  "Szakértői státusz",
  "Magabiztos jelenlét",
  "Bizalom mesterfokon",
  "Beszélgetésindító posztok",
  "Lélekig ható emailek",
  "Ellenállhatatlan ajánlatok",
  "Ügyfélmágnes",
  "Marketing, ami már te vagy",
  "Tartalom para nélkül",
  "Ügyfélszerzés görcs nélkül",
  "Egy hullámhosszon",
  "Szenvedélyből profit",
];

// Új feltöltött kép a Poszt-ötlet kártyához (hölgy hegyek tetején, lila naplemente)
const WOMAN_PORTRAIT =
  "https://customer-assets.emergentagent.com/job_mex-foundation/artifacts/qd5kaklk_6.png";

/* ═══════════════════════════════════════════════════════════════════
 * AnimatedHeadline - lassú, tiszta, egységes gradient + sima fade-blur-Y.
 * 6.5s interval, 1500ms transition, expo-out easing.
 * Az egész szó EGY gradientet kap, nem karakterenként.
 * Fix méretű stack-container - SOHA nem okoz layout shiftet.
 * ═══════════════════════════════════════════════════════════════════ */
function AnimatedHeadline() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex((i) => (i + 1) % HEADLINES.length);
    }, 6500);
    return () => clearInterval(id);
  }, []);

  return (
    <span
      data-testid="webinar-headline-rotator-wrap"
      className="relative inline-block w-full"
      style={{ minHeight: "1em" }}
    >
      {/* Mind a 14 headline absolute pozícióban egymás felett.
          Csak az aktuális opacity:1 + translateY:0; a többi opacity:0 + blur. */}
      {HEADLINES.map((text, i) => {
        const active = i === index;
        return (
          <span
            key={text}
            data-testid={active ? "landing-headline-rotator" : undefined}
            aria-hidden={!active}
            aria-label={active ? text : undefined}
            className="absolute inset-0 inline-block"
            style={{
              background:
                "linear-gradient(90deg, #5e50f7 0%, #4F46E5 35%, #29BEDA 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              opacity: active ? 1 : 0,
              transform: active ? "translateY(0)" : "translateY(18px)",
              filter: active ? "blur(0)" : "blur(10px)",
              transition:
                "opacity 1500ms cubic-bezier(0.19,1,0.22,1), transform 1500ms cubic-bezier(0.19,1,0.22,1), filter 1500ms cubic-bezier(0.19,1,0.22,1)",
              willChange: "opacity, transform, filter",
              letterSpacing: "-0.035em",
              whiteSpace: "nowrap",
              paddingBottom: "0.12em",
            }}
          >
            {text}
          </span>
        );
      })}
      {/* Sizing-element - láthatatlan, csak a wrapper szélességét biztosítja */}
      <span
        aria-hidden="true"
        className="invisible inline-block"
        style={{ letterSpacing: "-0.035em", whiteSpace: "nowrap", paddingBottom: "0.12em" }}
      >
        Beszélgetésindító posztok
      </span>
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * MagicalBackdrop - a TELJES hero szekció hátterét adja (NEM panel!).
 * Rétegei:
 *   R1: világos, levendulás/off-white seamless gradient (teljes section)
 *   R2: nagyon finom, halvány grid overlay (low opacity)
 *   R3: nagy diffúz glow blob-ok - jobbra dominál, balra is átfut
 *   R4: 4–6 vékony, áramló, glowing fényvonal
 *   R5: finom sparkle pontok (kolibri körül sűrűbben)
 *   R6: alul finom ködszerű/hullámos fénytér (jobb alsó dominál, balra elsimul)
 * NEM tartalmaz: kolibrit, kártyát, szöveget, gombot.
 * Nincs külön keret, nincs téglalap-háttér: organikusan simul.
 * ═══════════════════════════════════════════════════════════════════ */
function MagicalBackdrop() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* R1: seamless levendulás alap - egységes a teljes hero-ra */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(135deg, #F6F1FF 0%, #EFE7FE 24%, #ECEAFC 50%, #F1E7F8 78%, #F8EFFC 100%)",
          zIndex: 0,
        }}
      />
      {/* R2: nagyon halvány grid overlay */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(94,80,247,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(94,80,247,0.035) 1px, transparent 1px)",
          backgroundSize: "56px 56px",
          maskImage:
            "radial-gradient(ellipse at 65% 45%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0) 90%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 65% 45%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 55%, rgba(0,0,0,0) 90%)",
          zIndex: 1,
        }}
      />
      {/* R3: nagy diffúz glow blob-ok – jobbra dominál, balra is átfut */}
      <div
        className="absolute"
        style={{
          top: "-12%",
          right: "-8%",
          width: "62%",
          height: "85%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(94,80,247,0.42) 0%, rgba(94,80,247,0.16) 38%, rgba(94,80,247,0) 72%)",
          filter: "blur(20px)",
          zIndex: 2,
        }}
      />
      <div
        className="absolute"
        style={{
          top: "8%",
          right: "20%",
          width: "44%",
          height: "55%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(41,190,218,0.38) 0%, rgba(41,190,218,0.14) 42%, rgba(41,190,218,0) 72%)",
          filter: "blur(22px)",
          zIndex: 2,
        }}
      />
      <div
        className="absolute"
        style={{
          bottom: "-5%",
          right: "-2%",
          width: "55%",
          height: "60%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(124,58,237,0.32) 0%, rgba(124,58,237,0.12) 42%, rgba(124,58,237,0) 72%)",
          filter: "blur(26px)",
          zIndex: 2,
        }}
      />
      {/* glow balra átfutó */}
      <div
        className="absolute"
        style={{
          top: "18%",
          left: "-10%",
          width: "48%",
          height: "60%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(94,80,247,0.18) 0%, rgba(94,80,247,0.06) 45%, rgba(94,80,247,0) 75%)",
          filter: "blur(28px)",
          zIndex: 2,
        }}
      />
      <div
        className="absolute"
        style={{
          bottom: "10%",
          left: "8%",
          width: "42%",
          height: "44%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(251,113,185,0.16) 0%, rgba(251,113,185,0.05) 45%, rgba(251,113,185,0) 72%)",
          filter: "blur(30px)",
          zIndex: 2,
        }}
      />
      {/* nagyon enyhe arany akcent középen */}
      <div
        className="absolute"
        style={{
          top: "38%",
          left: "44%",
          width: "26%",
          height: "26%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(255,219,92,0.16) 0%, rgba(255,219,92,0) 70%)",
          filter: "blur(26px)",
          zIndex: 2,
        }}
      />
      {/* DISZKRÉT EXTRA GLOW közvetlenül a kolibri mögé (mélység + fókusz) */}
      <div
        className="absolute"
        style={{
          top: "30%",
          right: "16%",
          width: "32%",
          height: "44%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(124,58,237,0.28) 0%, rgba(94,80,247,0.16) 38%, rgba(94,80,247,0) 72%)",
          filter: "blur(24px)",
          zIndex: 2,
        }}
      />

      {/* R4: áramló fényvonalak (a jobb oldalra koncentráltak, de balra is átérnek) */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        style={{ zIndex: 3 }}
      >
        <defs>
          <linearGradient id="flow-grad-1" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#5e50f7" stopOpacity="0" />
            <stop offset="35%" stopColor="#5e50f7" stopOpacity="0.42" />
            <stop offset="70%" stopColor="#29BEDA" stopOpacity="0.32" />
            <stop offset="100%" stopColor="#29BEDA" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="flow-grad-2" x1="1" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#7C3AED" stopOpacity="0" />
            <stop offset="50%" stopColor="#7C3AED" stopOpacity="0.34" />
            <stop offset="100%" stopColor="#7C3AED" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="flow-grad-3" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#29BEDA" stopOpacity="0" />
            <stop offset="50%" stopColor="#29BEDA" stopOpacity="0.36" />
            <stop offset="100%" stopColor="#29BEDA" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="flow-grad-pink" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#FB7DD7" stopOpacity="0" />
            <stop offset="50%" stopColor="#FB7DD7" stopOpacity="0.22" />
            <stop offset="100%" stopColor="#FB7DD7" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* hosszú felső ívelő szellő balról-jobbra */}
        <path
          d="M -80 220 Q 360 100 720 200 T 1520 140"
          stroke="url(#flow-grad-1)"
          strokeWidth="1.4"
          fill="none"
          opacity="0.85"
        />
        {/* középső átkelő áram */}
        <path
          d="M -40 480 Q 320 380 720 500 T 1500 380"
          stroke="url(#flow-grad-2)"
          strokeWidth="1.2"
          fill="none"
          opacity="0.7"
        />
        {/* alsó vékony ív */}
        <path
          d="M 60 760 Q 380 660 760 720 T 1480 620"
          stroke="url(#flow-grad-1)"
          strokeWidth="0.9"
          fill="none"
          opacity="0.55"
        />
        {/* jobb oldali rövidebb cián szellő */}
        <path
          d="M 700 60 Q 980 120 1180 80 T 1500 100"
          stroke="url(#flow-grad-3)"
          strokeWidth="1.0"
          fill="none"
          opacity="0.7"
        />
        {/* pink finom akcent */}
        <path
          d="M 540 820 Q 880 760 1180 800 T 1480 760"
          stroke="url(#flow-grad-pink)"
          strokeWidth="0.8"
          fill="none"
          opacity="0.6"
        />
        {/* diszkrét extra ív a kolibri irányából a headline felé (tekintetvezetés) */}
        <path
          d="M 1240 460 Q 980 420 720 480 T 200 540"
          stroke="url(#flow-grad-2)"
          strokeWidth="0.7"
          fill="none"
          opacity="0.42"
        />
      </svg>

      {/* R5: sparkle pontok – sűrűbb a kolibri körül (jobb-közép), ritkább másutt */}
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        style={{ zIndex: 4 }}
      >
        {[
          // sűrű cluster jobb-közép (kolibri körül)
          [880, 240, 1.6, 0.92],
          [960, 320, 1.2, 0.78],
          [1040, 200, 1.4, 0.88],
          [1120, 360, 1.0, 0.7],
          [840, 420, 1.5, 0.85],
          [920, 500, 1.1, 0.72],
          [1000, 560, 1.3, 0.82],
          [1080, 480, 1.6, 0.9],
          [1180, 300, 1.0, 0.65],
          [1260, 240, 1.3, 0.78],
          [780, 320, 1.2, 0.75],
          [820, 580, 1.4, 0.85],
          [900, 660, 1.0, 0.6],
          [980, 720, 1.2, 0.7],
          [1060, 660, 1.5, 0.86],
          [1160, 540, 1.1, 0.7],
          [1240, 460, 1.3, 0.78],
          [1340, 380, 1.0, 0.65],
          // ritkább bal/közép
          [160, 180, 1.0, 0.55],
          [280, 280, 1.2, 0.62],
          [380, 160, 1.3, 0.7],
          [520, 240, 0.9, 0.5],
          [220, 480, 1.4, 0.72],
          [340, 580, 1.0, 0.55],
          [460, 660, 1.2, 0.62],
          [560, 760, 0.9, 0.5],
          [120, 720, 1.3, 0.68],
          [480, 100, 1.0, 0.55],
          [640, 380, 1.2, 0.6],
          [580, 540, 1.1, 0.58],
        ].map(([cx, cy, r, op], i) => (
          <g key={i}>
            <circle cx={cx} cy={cy} r={r} fill="#ffffff" opacity={op} />
            <circle cx={cx} cy={cy} r={r * 3} fill="#ffffff" opacity={op * 0.16} />
          </g>
        ))}
      </svg>

      {/* R6: alul finom, ködszerű hullámos fénytér (a glow blob — a hullámos
          SVG-elválasztó már a section végére van kihúzva, BottomWave-ként,
          hogy a backdrop seamless-en kiterjeszthető legyen a header alá is). */}
      <div
        className="absolute"
        style={{
          bottom: "-10%",
          right: "-5%",
          width: "70%",
          height: "32%",
          background:
            "radial-gradient(ellipse at 60% 40%, rgba(94,80,247,0.22) 0%, rgba(124,58,237,0.10) 38%, rgba(94,80,247,0) 72%)",
          filter: "blur(28px)",
          zIndex: 5,
        }}
      />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * BottomWave - a hero alját zárja le egy lágy hullámmal (a contact
 * szekció felé). Külön komponensben van, mert a MagicalBackdrop felfut
 * a header mögé is — ezt a hullámot viszont CSAK a hero alján akarjuk.
 * ═══════════════════════════════════════════════════════════════════ */
function BottomWave() {
  return (
    <svg
      aria-hidden="true"
      className="absolute bottom-0 left-0 w-full pointer-events-none"
      viewBox="0 0 1440 90"
      preserveAspectRatio="none"
      style={{ height: "80px", zIndex: 8, display: "block" }}
    >
      <path
        d="M 0 50 Q 180 0 360 40 T 720 38 T 1080 42 T 1440 28 L 1440 90 L 0 90 Z"
        fill="#F5EEFE"
      />
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * HummingbirdLayer - külön TRANSZPARENS PNG kolibri asset.
 * Csőre BALRA mutat → a bal oldali animált headline felé.
 * NEM túl jobbra: ~10% offset a hero-vizuál jobb-közepétől.
 * ═══════════════════════════════════════════════════════════════════ */
function HummingbirdLayer() {
  return (
    <img
      src="https://customer-assets.emergentagent.com/job_mex-foundation/artifacts/5i8lgumc_kolibri_transparent.png"
      alt=""
      aria-hidden="true"
      className="absolute pointer-events-none lh-hummingbird"
      // ABOVE-THE-FOLD: eager + high prioritás, NEM lazy-load
      loading="eager"
      decoding="async"
      fetchPriority="high"
      // POZÍCIÓ ÉS MÉRET PONTOSAN UGYANAZ MINT KORÁBBAN
      style={{
        top: "52%",
        right: "6%",
        transform: "translateY(-50%)",
        width: "min(100%, 770px)",
        height: "auto",
        aspectRatio: "1 / 1",
        objectFit: "contain",
        filter: "drop-shadow(0 42px 80px rgba(94,80,247,0.40))",
        zIndex: 4,
        animation: "lh-hummingbird-breathe 11s ease-in-out infinite",
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * FloatingCard - üvegszerű, prémium tartalomkártya (újratervezve)
 * Nagy radius, pill label, finom árnyékok, sok whitespace.
 * ═══════════════════════════════════════════════════════════════════ */
function FloatingCard({
  style,
  label,
  labelIcon: LabelIcon,
  labelColor = "#5e50f7",
  rotate = 0,
  delay = 0,
  testid,
  positionClass = "",
  children,
}) {
  return (
    <div
      data-testid={testid}
      className={`absolute lh-card ${positionClass}`}
      style={{
        ...style,
        transform: `${style?.transform || ""} rotate(${rotate}deg)`.trim(),
        animation: `lh-float 9s ease-in-out ${delay}s infinite`,
        zIndex: 5,
      }}
    >
      <div
        className="rounded-[26px] border overflow-hidden lh-card-inner"
        style={{
          background: "rgba(255,255,255,0.94)",
          backdropFilter: "blur(22px) saturate(120%)",
          WebkitBackdropFilter: "blur(22px) saturate(120%)",
          borderColor: "rgba(232,228,250,0.95)",
          boxShadow:
            "0 30px 90px -20px rgba(60,52,137,0.22), 0 8px 24px -8px rgba(60,52,137,0.10), 0 1px 0 rgba(255,255,255,0.85) inset",
        }}
      >
        {(label || LabelIcon) && (
          <div className="px-5 pt-4 pb-1 flex items-center lh-card-label">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10.5px] font-semibold tracking-[0.04em]"
              style={{
                background: `${labelColor}14`,
                color: labelColor,
              }}
            >
              {LabelIcon ? <LabelIcon size={12} weight="fill" /> : null}
              {label}
            </span>
          </div>
        )}
        {children}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * HeroVisual - VALÓDI webes kompozíció (NEM screenshot háttér).
 * Tartalmazza: kolibri-backdrop (beleolvad a háttérbe), 5 prémium kártya
 * ═══════════════════════════════════════════════════════════════════ */
function HeroVisual() {
  return (
    <div
      data-testid="hero-visual"
      className="relative w-full lh-hero-visual"
      style={{
        height: "min(82vh, 720px)",
        minHeight: "560px",
      }}
    >
      <style>{`
        @keyframes lh-float {
          0%, 100% { translate: 0 0; }
          50%      { translate: 0 -8px; }
        }
        @keyframes lh-hummingbird-breathe {
          0%, 100% { transform: translateY(-50%) scale(1) rotate(0deg); }
          50%      { transform: translateY(-52%) scale(1.015) rotate(-0.6deg); }
        }
        /* ───── MOBIL (<768px) BAL OLDAL: középre + nagyobb méretek ───── */
        @media (max-width: 767.98px) {
          .lh-kicker-wrap { text-align: center; }
          .lh-left-col { text-align: center; }
          .lh-left-col > p, .lh-left-col > div { margin-left: auto !important; margin-right: auto !important; }
          .lh-kicker { font-size: 14px !important; }
          .lh-headline { text-align: center !important; }
          .lh-headline-anim { font-size: 40px !important; }
          .lh-headline-fixed { font-size: 38px !important; }
          .lh-subtitle { font-size: 19px !important; text-align: center !important; max-width: 100% !important; }
          .lh-cta {
            display: inline-flex !important;
            font-size: 19px !important;
            padding: 22px 38px !important;
          }
          .lh-powered { justify-content: center !important; font-size: 16px !important; }
        }
        /* MOBIL (<768px): kompakt, levegős kompozíció */
        @media (max-width: 767.98px) {
          .lh-hero-visual { height: 820px !important; min-height: 820px !important; }
          .lh-hummingbird { width: 96% !important; right: 2% !important; }
          .lh-card { transform-origin: center center; }
          .lh-card-1 { top: 0% !important; right: 0% !important; width: 184px !important; }
          .lh-card-2 { top: 8% !important; left: 2% !important; width: 172px !important; }
          .lh-card-3 { top: 64% !important; left: -3% !important; width: 176px !important; }
          .lh-card-4 { bottom: 2% !important; left: 10% !important; width: 184px !important; }
          .lh-card-5 { bottom: 0% !important; right: 0% !important; width: 174px !important; }
          .lh-card-inner { border-radius: 20px !important; }
          .lh-card-title { font-size: 13.5px !important; }
          .lh-card-body { font-size: 11.5px !important; }
          .lh-card-btn { font-size: 11.5px !important; padding: 6px 12px !important; }
          .lh-card-media { height: 108px !important; }
          .lh-card-label { padding: 12px 16px 2px !important; }
        }
        /* KIS MOBIL (<400px) */
        @media (max-width: 399.98px) {
          .lh-hero-visual { height: 740px !important; min-height: 740px !important; }
          .lh-card-1 { width: 160px !important; }
          .lh-card-2 { width: 150px !important; }
          .lh-card-3 { width: 154px !important; }
          .lh-card-4 { width: 160px !important; left: 6% !important; }
          .lh-card-5 { width: 150px !important; }
        }
      `}</style>

      {/* RÉTEG: TRANSZPARENS KOLIBRI ASSET (csőre balra mutat a címre) */}
      <HummingbirdLayer />

      {/* RÉTEG: VALÓDI HTML kártyák - rétegelve a kolibri FÖLÖTT */}

      {/* Kártya 1: Poszt ötlet - JOBB FELSŐ */}
      <FloatingCard
        testid="card-post-idea"
        positionClass="lh-card-1"
        style={{ top: "0%", right: "0%", width: "240px" }}
        rotate={-2.2}
        delay={0}
        label="POSZT ÖTLET"
        labelIcon={Sparkle}
        labelColor="#5e50f7"
      >
        <div className="px-5 pt-2 pb-3 text-[14.5px] font-bold text-[#1B1733] leading-[1.25] tracking-[-0.01em] lh-card-title">
          Vonzó ötlet a megfelelő ügyfeleknek
        </div>
        <div
          className="mx-4 mb-3 rounded-2xl overflow-hidden h-[148px] bg-no-repeat lh-card-media"
          style={{
            backgroundImage: `url('${WOMAN_PORTRAIT}')`,
            backgroundSize: "175% auto",
            backgroundPosition: "center 58%",
          }}
        />
        <div className="px-5 pb-4 flex items-center gap-4 text-[12px] text-[#7065A0] font-medium lh-card-body">
          <span className="inline-flex items-center gap-1.5"><Heart size={13} weight="fill" color="#FB7185" /> 128</span>
          <span className="inline-flex items-center gap-1.5"><ChatCircle size={13} weight="fill" color="#5e50f7" /> 26</span>
          <span className="inline-flex items-center gap-1.5"><ShareNetwork size={13} weight="fill" color="#29BEDA" /> 8</span>
        </div>
      </FloatingCard>

      {/* Kártya 2: Hírlevél - FELJEBB és HÁTRÉBB (kolibrinek utat adva) */}
      <FloatingCard
        testid="card-newsletter"
        positionClass="lh-card-2"
        style={{ top: "-2%", left: "10%", width: "224px", opacity: 0.96 }}
        rotate={2.4}
        delay={1.2}
        label="HÍRLEVÉL"
        labelIcon={EnvelopeOpen}
        labelColor="#29BEDA"
      >
        <div className="px-5 pt-1.5 text-[14px] font-semibold text-[#1B1733] leading-snug lh-card-body">
          Szia! <span style={{ filter: "saturate(1.1)" }}>👋</span>
        </div>
        <div className="px-5 pt-1 pb-4 text-[13.5px] text-[#3F365E] leading-[1.45] lh-card-body">
          Egy gondolat, amely megváltoztathatja a napodat.
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white px-4 py-2 rounded-full lh-card-btn"
            style={{
              background: "linear-gradient(135deg, #5e50f7 0%, #4F46E5 100%)",
              boxShadow: "0 8px 20px -6px rgba(94,80,247,0.5)",
            }}
          >
            Elolvasom <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </FloatingCard>

      {/* Kártya 3: Landing oldal - LEJJEBB (kolibri körüli levegősebb kompozíció) */}
      <FloatingCard
        testid="card-landing"
        positionClass="lh-card-3"
        style={{ top: "52%", left: "-5%", width: "230px" }}
        rotate={-3.0}
        delay={2.4}
        label="LANDING OLDAL"
        labelIcon={Globe}
        labelColor="#29BEDA"
      >
        <div className="px-5 pt-2 pb-2 text-[18px] font-bold text-[#1B1733] leading-[1.1] tracking-[-0.02em] lh-card-title">
          Több ügyfél,<br />kevesebb zaj.
        </div>
        <div className="px-5 pb-4 text-[12.5px] text-[#5B5380] leading-[1.5] lh-card-body">
          Világos üzenet. Erős hatás. Folyamatos növekedés.
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white px-4 py-2 rounded-full lh-card-btn"
            style={{
              background: "linear-gradient(135deg, #29BEDA 0%, #1FA9C9 100%)",
              boxShadow: "0 8px 20px -6px rgba(41,190,218,0.5)",
            }}
          >
            Kialakítod <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </FloatingCard>

      {/* Kártya 4: Értékesítési üzenet - LEJJEBB (alsó perem felé) */}
      <FloatingCard
        testid="card-sales-msg"
        positionClass="lh-card-4"
        style={{ bottom: "-3%", left: "22%", width: "240px" }}
        rotate={1.6}
        delay={0.6}
        label="ÉRTÉKESÍTÉSI ÜZENET"
        labelIcon={PaperPlaneTilt}
        labelColor="#FB7185"
      >
        <div className="px-5 pt-1.5 text-[14px] font-semibold text-[#1B1733] leading-snug lh-card-body">
          Szia! <span style={{ filter: "saturate(1.1)" }}>👋</span>
        </div>
        <div className="px-5 pt-1 pb-4 text-[13.5px] text-[#3F365E] leading-[1.45] lh-card-body">
          Van egy ötletem, ami segíthet gyorsabban elérni a céljaidat.
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-white px-4 py-2 rounded-full lh-card-btn"
            style={{
              background: "linear-gradient(135deg, #FB7185 0%, #FBA94B 100%)",
              boxShadow: "0 8px 20px -6px rgba(251,113,133,0.45)",
            }}
          >
            Megnézem <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </FloatingCard>

      {/* Kártya 5: Eredmény - JOBB ALSÓ */}
      <FloatingCard
        testid="card-result"
        positionClass="lh-card-5"
        style={{ bottom: "4%", right: "-2%", width: "226px" }}
        rotate={-1.4}
        delay={1.8}
        label="EREDMÉNY"
        labelIcon={TrendUp}
        labelColor="#5e50f7"
      >
        <div className="px-5 pt-2 pb-2 text-[18px] font-bold text-[#1B1733] leading-[1.1] tracking-[-0.02em] lh-card-title">
          Magasabb<br />konverzió
        </div>
        <div className="px-5 pb-3 text-[12.5px] text-[#5B5380] leading-[1.5] lh-card-body">
          Jobb ajánlatok, tisztább érték, több elköteleződés.
        </div>
        {/* Mini emelkedő chart */}
        <div className="px-5 pb-3">
          <svg viewBox="0 0 160 36" className="w-full h-7">
            <defs>
              <linearGradient id="chart-grad" x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor="#5e50f7" stopOpacity="0.06" />
                <stop offset="100%" stopColor="#5e50f7" stopOpacity="0.42" />
              </linearGradient>
            </defs>
            <path d="M 0 30 L 30 25 L 60 26 L 90 18 L 120 12 L 160 4 L 160 36 L 0 36 Z" fill="url(#chart-grad)" />
            <path d="M 0 30 L 30 25 L 60 26 L 90 18 L 120 12 L 160 4" fill="none" stroke="#5e50f7" strokeWidth="1.8" strokeLinecap="round" />
            <circle cx="160" cy="4" r="2.8" fill="#5e50f7" />
          </svg>
        </div>
        <div className="px-5 pb-5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-[#5e50f7] px-4 py-2 rounded-full border border-[#E0DAFA] bg-white lh-card-btn"
            style={{ boxShadow: "0 4px 12px -4px rgba(94,80,247,0.18)" }}
          >
            Részletek <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </FloatingCard>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * Header — 3 oszlopos vízszintes ritmus desktopon:
 *   bal: MEX Magic logó | közép: dátum-pill | jobb: countdown
 * Mobilon: vertikálisan stackelődik (logo → pill → countdown), gap-3.
 * Belépés gomb eltávolítva (webinar fókusz).
 * ═══════════════════════════════════════════════════════════════════ */
function LandingHeader() {
  return (
    <header
      data-testid="webinar-header"
      className="w-full px-6 sm:px-10 lg:px-16 pt-6 pb-1 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3 md:gap-6 relative z-20"
    >
      {/* Bal: logó */}
      <a
        href="/"
        className="flex items-center gap-3.5 justify-self-center md:justify-self-start"
        data-testid="webinar-logo"
      >
        <div
          className="w-12 h-12 rounded-2xl flex items-center justify-center shadow-[0_12px_32px_rgba(94,80,247,0.36)]"
          style={{ background: "linear-gradient(135deg, #5e50f7 0%, #3a2fd3 100%)" }}
        >
          <Sparkle size={24} weight="fill" color="#fff" />
        </div>
        <div className="font-heading text-[24px] sm:text-[28px] tracking-tight text-[#2B2545] font-bold">
          MEX <span className="text-[#5e50f7]">Magic</span>
        </div>
      </a>

      {/* Közép: dátum-pill */}
      <div
        data-testid="webinar-date-pill"
        className="inline-flex items-center gap-2.5 px-6 py-3 rounded-full justify-self-center"
        style={{
          background: "rgba(255,255,255,0.78)",
          border: "1px solid rgba(94,80,247,0.24)",
          color: "#3C3489",
          fontSize: "15px",
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          boxShadow: "0 8px 22px -8px rgba(94,80,247,0.22)",
        }}
      >
        <span style={{ fontWeight: 600 }}>2026. augusztus 13. csütörtök, 19:00</span>
      </div>

      {/* Jobb: countdown */}
      <div className="justify-self-center md:justify-self-end">
        <WebinarCountdown />
      </div>
    </header>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * ContactForm
 * ═══════════════════════════════════════════════════════════════════ */
function ContactForm() {
  const [form, setForm] = useState({ topic: "", name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Tölts ki minden mezőt.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        topic: form.topic.trim(),
        message: form.message.trim(),
      });
      setSent(true);
      toast.success("Köszönjük! Megkaptuk az üzeneted, hamarosan jelentkezünk.");
      setForm({ topic: "", name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Nem sikerült elküldeni. Próbáld újra.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <div
        data-testid="contact-success"
        className="max-w-xl mx-auto rounded-3xl bg-white border border-[#E6E1FA] p-10 text-center shadow-[0_30px_80px_rgba(60,52,137,0.10)]"
      >
        <div className="mx-auto mb-5 w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "rgba(94,80,247,0.10)" }}>
          <CheckCircle size={28} weight="fill" color="#5e50f7" />
        </div>
        <h3 className="font-heading text-[24px] text-[#2B2545] mb-2">Köszönjük!</h3>
        <p className="text-[15px] text-[#52525B] leading-relaxed">Megkaptuk az üzeneted, hamarosan jelentkezünk.</p>
      </div>
    );
  }

  const inputBase =
    "w-full rounded-xl bg-white border border-[#DED7F5] px-4 py-3.5 text-[15px] text-[#2B2545] placeholder:text-[#9890b8] focus:outline-none focus:border-[#5e50f7] focus:ring-4 focus:ring-[#5e50f7]/10 transition-all";

  return (
    <form onSubmit={onSubmit} data-testid="contact-form" className="max-w-xl mx-auto rounded-3xl bg-white border border-[#E6E1FA] p-7 sm:p-10 shadow-[0_30px_80px_rgba(60,52,137,0.10)]">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "rgba(94,80,247,0.10)" }}>
          <ChatCircleDots size={22} color="#5e50f7" weight="fill" />
        </div>
        <div>
          <h3 className="font-heading text-[24px] sm:text-[28px] text-[#2B2545] leading-tight">Írj nekünk</h3>
          <p className="text-[13px] sm:text-[14px] text-[#6A6488] leading-relaxed">Kérdésed van, vagy szeretnél hozzáférést kérni? Írj nekünk, és visszajelzünk.</p>
        </div>
      </div>

      <div className="space-y-3.5">
        <div className="relative">
          <Tag size={16} color="#8e84b8" className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <select data-testid="contact-topic" value={form.topic} onChange={onChange("topic")} className={`${inputBase} pl-11 appearance-none cursor-pointer`}>
            <option value="">Válassz témát...</option>
            <option value="Várólista / hozzáférés">Várólista / hozzáférés</option>
            <option value="Technikai kérdés">Technikai kérdés</option>
            <option value="Együttműködés">Együttműködés</option>
            <option value="Egyéb kérdés">Egyéb kérdés</option>
          </select>
        </div>
        <div className="relative">
          <UserIcon size={16} color="#8e84b8" className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input data-testid="contact-name" value={form.name} onChange={onChange("name")} placeholder="Add meg a neved" className={`${inputBase} pl-11`} required />
        </div>
        <div className="relative">
          <EnvelopeSimple size={16} color="#8e84b8" className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input data-testid="contact-email" value={form.email} onChange={onChange("email")} type="email" placeholder="email@pelda.hu" className={`${inputBase} pl-11`} required />
        </div>
        <div className="relative">
          <PencilSimple size={16} color="#8e84b8" className="absolute left-4 top-4 pointer-events-none" />
          <textarea data-testid="contact-message" value={form.message} onChange={onChange("message")} placeholder="Írd le, miben segíthetünk..." rows={5} className={`${inputBase} pl-11 resize-none`} required />
        </div>
        <button
          type="submit"
          disabled={submitting}
          data-testid="contact-submit"
          className="w-full group inline-flex items-center justify-center gap-2 mt-2 px-7 py-4 rounded-2xl font-semibold text-[15px] text-white transition-all hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
          style={{
            background: "linear-gradient(135deg, #5e50f7 0%, #7C3AED 52%, #4F46E5 100%)",
            boxShadow: "0 16px 36px rgba(94,80,247,0.32), 0 0 0 1px rgba(255,255,255,0.20) inset",
          }}
        >
          {submitting ? "Küldés..." : "Üzenet küldése"}
          {!submitting && (<ArrowRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5" />)}
        </button>
      </div>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════
 * FŐKOMPONENS
 * ═══════════════════════════════════════════════════════════════════ */
export default function WebinarLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginDenied, setLoginDenied] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);

  // Meta Pixel — alap PageView (Lead event NINCS, egyéni konverzió
  // Events Manager-ből, Thank You URL alapján kerül beállításra).
  useEffect(() => {
    trackPageView("/webinar-landing");
  }, []);

  const openRegister = () => setRegisterOpen(true);
  const closeRegister = () => setRegisterOpen(false);

  const handleLoginClose = () => {
    setLoginOpen(false);
    setLoginDenied(null);
    // Ha /login direkt URL-en érkezett a user, vigyük vissza a / -ra.
    if (location.pathname === "/login" || location.search.includes("denied=")) {
      navigate("/", { replace: true });
    }
  };

  // NOTE: /webinar PUBLIC route — bejelentkezett user is itt marad,
  // SEMMI auto-redirect a /dashboard-ra. (Korábbi useEffect kivéve.)

  // Egységes prémium belépési élmény: ha valaki direktben nyitja meg a
  // /login URL-t, automatikusan ezt a modalt nyitjuk a landing fölött.
  useEffect(() => {
    if (location.pathname === "/login" || location.search.includes("login=1")) {
      setLoginOpen(true);
    }
    // Allowlist denial visszairányítás query-paramban (?denied=not_invited|inactive)
    const params = new URLSearchParams(location.search);
    const kind = params.get("denied");
    const msg = params.get("msg");
    if (kind) {
      setLoginDenied({ kind, message: msg || "" });
      setLoginOpen(true);
    }
  }, [location.pathname, location.search]);

  const bgStyle = useMemo(
    () => ({
      background:
        "radial-gradient(60% 50% at 75% 22%, rgba(94,80,247,0.12) 0%, rgba(255,255,255,0) 60%), radial-gradient(50% 40% at 20% 35%, rgba(41,190,218,0.08) 0%, rgba(255,255,255,0) 65%), radial-gradient(60% 70% at 90% 90%, rgba(255,219,92,0.07) 0%, rgba(255,255,255,0) 60%), linear-gradient(180deg, #F8F6FE 0%, #FBFAFF 100%)",
      minHeight: "100vh",
    }),
    []
  );

  return (
    <div data-testid="webinar-page" style={bgStyle} className="font-sans relative overflow-x-hidden" data-font="geist">
      {/* Finom rácsháló */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(rgba(94,80,247,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(94,80,247,0.05) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)",
          WebkitMaskImage: "radial-gradient(ellipse at top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 70%)",
        }}
      />

      <div className="relative z-10">
        {/* ─── HERO + HEADER COMMON WRAPPER ───
            A MagicalBackdrop ennek a wrappernek a hátterét adja, így a
            kolibri-glow / lavender gradient SEAMLESS-en felfut a header
            mögé is — nincs külön „világos sáv" a tetején. */}
        <div className="relative">
          {/* Backdrop: header + hero közös hátterét egyetlen rétegként rendereli */}
          <MagicalBackdrop />

          <div className="relative z-20">
            <LandingHeader />
          </div>

          {/* HERO – tartalom rétegek a backdrop felett, BottomWave a section alján */}
          <section
            data-testid="hero-section"
            className="relative px-6 sm:px-10 lg:px-16 pt-8 lg:pt-12 pb-24 lg:pb-32 overflow-hidden z-10"
          >
            <div className="relative max-w-[1440px] mx-auto grid grid-cols-1 lg:grid-cols-[46%_54%] gap-10 lg:gap-12 items-center">
            {/* ─── BAL: szöveg + CTA — bal block 1.5 címsornyit feljebb (translate-y), 
                 CTA wrapper kis pozitív mt-tel, így a CTA csak ~1 címsornyit megy fel ─── */}
            <div className="pt-0 relative z-10 lh-left-col lg:-translate-y-[60px]">
              {/* Kicker — INGYENES AI MARKETING MESTERKURZUS (logó alatt, levegővel) */}
              <div
                data-testid="webinar-kicker"
                className="lh-kicker-wrap mb-4"
                style={{
                  fontSize: 13,
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  color: "#3C3489",
                  fontWeight: 600,
                }}
              >
                Ingyenes AI marketing mesterkurzus
              </div>

              <h1
                data-testid="webinar-headline"
                className="font-heading lh-headline"
                style={{
                  fontWeight: 700,
                  lineHeight: 1.14,
                  letterSpacing: "-0.03em",
                  color: "#1B1733",
                  fontSize: "clamp(30px, 3.7vw, 48px)",
                }}
              >
                Ügyfélszerző anyagok pár kattintással,
                <br />
                <span
                  style={{
                    background:
                      "linear-gradient(90deg, #5e50f7 0%, #4F46E5 35%, #29BEDA 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  a saját hangodon
                </span>
              </h1>

              <p
                data-testid="webinar-subtitle"
                className="mt-5 mb-7 max-w-[560px] lh-subtitle"
                style={{ color: "#433C66", fontSize: "clamp(15.5px, 1.15vw, 19px)", lineHeight: 1.65 }}
              >
                Ezen az ingyenes mesterkurzuson megmutatom, hogyan tudsz gyorsabban, tisztábban és magabiztosabban olyan marketinganyagokat létrehozni, amelyek nem robotízűek, nem sablonosak, nem nyomulósak, mégis segítenek több figyelmet, beszélgetést és érdeklődőt hozni.
              </p>

              {/* Nagy lila CTA — popup modalt nyit. Wrapper lg:mt-[30px]
                  hogy a CTA összesen csak ~1 címsorral menjen feljebb (a többi 1.5-tel),
                  + extra 10px lejjebb a felhasználói kérésre. */}
              <div className="lg:mt-[30px]">
                <WebinarCtaButton onClick={openRegister} />
              </div>
            </div>

            {/* ─── JOBB: kolibri + kártyák — picit feljebb (16px-szel) ─── */}
            <div className="relative mt-6 lg:mt-8">
              <HeroVisual />
            </div>
          </div>

          {/* Lágy hullám-elválasztó a contact szekció felé — csak a hero
              alján, nem a header alatt. */}
          <BottomWave />
        </section>
        </div>{/* /HERO + HEADER COMMON WRAPPER */}

        {/* CONTACT - eredeti contact-szekció eltávolítva, helyébe a
            webinar-specifikus szekciók kerültek. */}

        {/* ─── WEBINAR-SPECIFIKUS SZEKCIÓK ─────────────────────────── */}
        <WebinarValueSection />
        <WebinarLiveOnlyCallout />
        <WebinarAboutSection onOpenRegister={openRegister} />

        {/* MINIMÁL FOOTER — jogi link sor */}
        <footer
          data-testid="webinar-footer"
          className="px-6 sm:px-10 lg:px-16 py-7 text-center text-[12.5px] text-[#7B7596]"
          style={{
            background: "#EDE3F6",
            borderTop: "1px solid rgba(94,80,247,0.06)",
          }}
        >
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
            <a href="/aszf" data-testid="footer-link-aszf" className="hover:text-[#5e50f7] transition-colors">
              ÁSZF
            </a>
            <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
            <span>MEX Magic • All rights reserved.</span>
            <span aria-hidden="true" style={{ opacity: 0.6 }}>·</span>
            <a href="/adatkezelesi-tajekoztato" data-testid="footer-link-gdpr" className="hover:text-[#5e50f7] transition-colors">
              Adatkezelési tájékoztató
            </a>
          </div>
          {/* Meta disclaimer — 2 sorban, kiegyensúlyozott törés */}
          <div
            data-testid="footer-meta-disclaimer"
            className="mx-auto mt-4 text-center"
            style={{ fontSize: 11.5, lineHeight: 1.55, maxWidth: 720, color: "#7B7596" }}
          >
            <div>
              Ez az oldal nem része a Facebooknak vagy a Meta Platforms Inc.-nek, és nem áll velük kapcsolatban.
            </div>
            <div>
              A Facebook a Meta Platforms Inc. védjegye.
            </div>
          </div>
        </footer>
      </div>

      {/* Belépési modal — Google OAuth */}
      <LoginModal open={loginOpen} onClose={handleLoginClose} denied={loginDenied} />

      {/* Webinar regisztrációs modal — Keresztnév + E-mail */}
      <WebinarRegisterModal open={registerOpen} onClose={closeRegister} />

      {/* Meta Pixel — noscript fallback PageView */}
      <noscript>
        <img
          height="1"
          width="1"
          style={{ display: "none" }}
          alt=""
          src={METAPIXEL_NOSCRIPT_URL}
        />
      </noscript>
    </div>
  );
}
